const MASS_UNITS=new Set(['g','kg','oz','lb']);

function required(value,label,max=160){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optionalId(value,label){
  if(value==null||value==='')return null;
  return required(value,label,128);
}

function nonNegativeInteger(value,label,{max=1000000}={}){
  const number=Number(value);
  if(!Number.isInteger(number)||number<0||number>max)throw new TypeError(label+' is invalid');
  return number;
}

function optionalPositive(value,label){
  if(value==null||value==='')return null;
  const number=Number(value);
  if(!Number.isFinite(number)||number<=0)throw new TypeError(label+' must be positive');
  return number;
}

function optionalTimestamp(value,label){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(Number.isNaN(Date.parse(text)))throw new TypeError(label+' must be an ISO timestamp');
  return text;
}

function idOrGenerated(value){
  const id=String(value||'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

export function createSmokingItem({
  id,
  userId,
  sessionId,
  catalogueProductCode=null,
  customProductId=null,
  nameSnapshot,
  quantity=null,
  mass=null,
  massUnit=null,
  sortOrder=0,
  deletedAt=null
}={}){
  const catalogue=optionalId(catalogueProductCode,'catalogueProductCode');
  const custom=optionalId(customProductId,'customProductId');
  if(Boolean(catalogue)===Boolean(custom)){
    throw new TypeError('Exactly one catalogue or custom product reference is required');
  }

  const massValue=optionalPositive(mass,'mass');
  const unit=massUnit==null||massUnit===''?null:String(massUnit).trim().toLowerCase();
  if((massValue==null)!=(unit==null))throw new TypeError('mass and massUnit must be provided together');
  if(unit&&!MASS_UNITS.has(unit))throw new TypeError('Invalid massUnit');

  const quantityValue=quantity==null||quantity===''?null:Number(quantity);
  if(quantityValue!=null&&(!Number.isFinite(quantityValue)||quantityValue<=0||quantityValue>100000)){
    throw new TypeError('quantity must be positive');
  }

  return Object.freeze({
    id:idOrGenerated(id),
    userId:required(userId,'userId',128),
    sessionId:required(sessionId,'sessionId',128),
    catalogueProductCode:catalogue,
    customProductId:custom,
    nameSnapshot:required(nameSnapshot,'nameSnapshot',160),
    quantity:quantityValue,
    mass:massValue,
    massUnit:unit,
    sortOrder:nonNegativeInteger(sortOrder,'sortOrder'),
    deletedAt:optionalTimestamp(deletedAt,'deletedAt')
  });
}

export function updateSmokingItem(item,changes={}){
  const current=createSmokingItem(item);
  return createSmokingItem({
    ...current,
    ...changes,
    id:current.id,
    userId:current.userId,
    sessionId:current.sessionId
  });
}

export function removeSmokingItem(item,{at=new Date().toISOString()}={}){
  return updateSmokingItem(item,{deletedAt:optionalTimestamp(at,'at')});
}

export function orderSmokingItems(items,{includeDeleted=false}={}){
  if(!Array.isArray(items))throw new TypeError('items must be an array');
  return Object.freeze(
    items
      .map(createSmokingItem)
      .filter(item=>includeDeleted||!item.deletedAt)
      .sort((a,b)=>a.sortOrder-b.sortOrder||String(a.id).localeCompare(String(b.id)))
  );
}

export function smokingItemToRow(item){
  const value=createSmokingItem(item);
  return Object.freeze({
    id:value.id,
    user_id:value.userId,
    session_id:value.sessionId,
    catalogue_product_code:value.catalogueProductCode,
    custom_product_id:value.customProductId,
    name_snapshot:value.nameSnapshot,
    quantity:value.quantity,
    mass:value.mass,
    mass_unit:value.massUnit,
    sort_order:value.sortOrder,
    deleted_at:value.deletedAt
  });
}

export function smokingItemFromRow(row={}){
  return createSmokingItem({
    id:row.id,
    userId:row.user_id,
    sessionId:row.session_id,
    catalogueProductCode:row.catalogue_product_code,
    customProductId:row.custom_product_id,
    nameSnapshot:row.name_snapshot,
    quantity:row.quantity,
    mass:row.mass,
    massUnit:row.mass_unit,
    sortOrder:row.sort_order,
    deletedAt:row.deleted_at
  });
}
